import { Box, Button, Chip, CircularProgress, Container, Divider, Grid, List, ListItem, ListItemText, Paper, Stack, TextField, Typography } from "@mui/material";
import { PageProps } from "../../_types/page.props.interface";
import { StyleHelper } from "../../helpers/styler-helper";
import LiveBroadcast, { LiveBroadcastProps } from "../../components/streamer";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { StreamService } from "../../services/stream.service";
import LoadingBtn from "../../components/loading-btn";
import LoadingButtonMUI from "../../components/loading-btn copy";
import Actions from "../../redux/actions";
import { CarnivoreStream } from "../../_types/stream.type";
import ElapsedTimeChip from "../../components/elapsed-time";
import { StreamStatus } from "../../_types/stream-status.type";
import { PersonOffOutlined, PersonOutline } from "@mui/icons-material";
import CarnivoreStreamComponent from "../../components/stream-meta";


export default function GoLivePage(props: PageProps) {

    const [isStreamLive, setIsStreamLive] = useState(false)
    const [streamAuth, setStreamAuth] = useState<{ key: string, endpoint: string } | null>(null)
    const sessionIdRef = useRef<string>('')

    const [configLoading, setConfigLoading] = useState(false)

    const [startTime, setStartTime] = useState<Date | undefined>()

    const [currentStream, setCurrentStream] = useState<CarnivoreStream | null>(null)
    const [streamStatus, setStreamStatus] = useState<StreamStatus | null>(null)

    const dispatch = useDispatch()
    const streamService = new StreamService(dispatch)
    const [password, setPassword] = useState('')


    const streamConfig = useMemo(() => ({
        maxResolution: { width: 1280, height: 720 },
        maxFramerate: 30,
        maxBitrate: 2400,
    }), []);

    useEffect(() => {
        // only start when live *and* we have a sessionId
        const sessionId = currentStream?.sessionId
        if (!isStreamLive || !sessionId) return

        const updateStream = async () => {
            const data = await streamService.getStreamStats(sessionId)
            if (data.currentStream) setCurrentStream(data.currentStream)
            if (data.status) setStreamStatus(data.status)
        }

        updateStream()                       // initial call
        const intervalId = setInterval(updateStream, 6500)
        return () => clearInterval(intervalId)
    }, [isStreamLive, currentStream?.sessionId])


    const handleBroadcastStateChange: LiveBroadcastProps['onBroadcastStateChange'] = (state, info) => {

        if (state === 'live') {
            setIsStreamLive(true)
            streamService.startStream().then(data => {
                if (data.currentStream) {
                    setCurrentStream(data.currentStream)
                    sessionIdRef.current = data.currentStream.sessionId
                }
                if (data.status) {
                    setStreamStatus(data.status)
                }
            })
            setStartTime(new Date())
        }
        if (state === 'error') {
            dispatch(Actions.setError('An error occurred starting the stream, contact tyler'))
            setIsStreamLive(false)
            setCurrentStream(null)
            setStreamAuth(null)

        }
        if (state === 'idle') {
            setStreamAuth(null)
            setCurrentStream(null)
            setIsStreamLive(false)
            sessionIdRef.current = ""
            streamService.stopStream()

        }

        console.log('Broadcast state:', state, info);
        // e.g. switch UI, show spinner, show “Live” badge, display error, etc.
    };


    const _handleStreamConfig = async (e: FormEvent) => {
        e.preventDefault()
        setConfigLoading(true)
        const res = await streamService.getStreamConfig(password)
        if (res.streamKey) {
            if (res.tokenExpires) {
                const target = new Date(res.tokenExpires)
                const now = new Date();
                const diffMs = target.getTime() - now.getTime();
                const twoHoursMs = 2 * 60 * 60 * 1000;
                const isLessThan2Hours = diffMs > 0 && diffMs <= twoHoursMs;
                if (isLessThan2Hours) {
                    alert("Your session will expire in less than 2 hours. If you plan on streaming for longer than 2 hours, please logout first before continuing")
                }
            }
            setStreamAuth({
                key: res.streamKey,
                endpoint: res.endpoint
            })
            setPassword('')

        }
        setConfigLoading(false)
    }

    return <Container className='p-3'>
        <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'baseline'}
        >
            <Box>
                <Typography variant='h2' textAlign={'start'}>
                    Go Live
                </Typography>
                <Typography variant='body1' textAlign={'start'}>
                    Start a live stream
                </Typography>
            </Box>
        </Box>

        <Divider />
        <StyleHelper.VerticalSpace />
        {!streamAuth && <>
            <Grid container justifyContent={'center'}>
                <Grid item sm={6}>
                    <Paper elevation={3}>
                        <Box p={3} textAlign={'left'}>

                            <Typography variant="subtitle1" fontWeight={'bold'}>Enter your Password</Typography>
                            <Typography variant="body2">As an added layer of security, enter your password again to start a stream.</Typography>
                            <StyleHelper.VerticalSpace />
                            <form onSubmit={_handleStreamConfig}>
                                <TextField
                                    label='Password'
                                    required
                                    variant='outlined'
                                    type="password"
                                    fullWidth
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                />
                                <StyleHelper.VerticalSpace />
                                <LoadingButtonMUI variant="contained" disabled={configLoading} type="submit" loading={configLoading} >Submit</LoadingButtonMUI>
                            </form>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </>}
        {streamAuth && <>
            <Grid container spacing={2}>
                <Grid item xs={6} textAlign={'left'}>
                    <LiveBroadcast
                        streamKey={streamAuth.key}
                        ingestEndpoint={streamAuth.endpoint}
                        streamConfig={streamConfig}
                        onBroadcastStateChange={handleBroadcastStateChange}
                    />
                    {isStreamLive && <Box m={1}> <Stack spacing={2} direction={'row'}>
                        {startTime && <ElapsedTimeChip startDate={startTime?.toISOString()} />}
                        {streamStatus && <Chip
                            icon={<PersonOutline />}
                            color="primary"
                            label={streamStatus.viewerCount}
                            sx={{ alignSelf: 'flex-start' }}
                        />}

                    </Stack></Box>}
                    <StyleHelper.VerticalSpace />
                    <Typography variant="subtitle1" fontWeight={'bold'}>Stream Cheat Sheet</Typography>
                    <List
                        component="ol"
                        sx={{
                            listStyleType: 'decimal',
                            pl: 2,              // indent to align numbers
                            '& li': {
                                display: 'list-item'
                            }
                        }}
                    >
                        <ListItem component="li">
                            <ListItemText primary="If you do not see your camera or microphone, please allow permissions for this website to access your camera and microphone" />
                        </ListItem>
                        <ListItem component="li">
                            <ListItemText primary="You must select your camera and microphone to begin the stream" />
                        </ListItem>
                        <ListItem component="li">
                            <ListItemText primary="You will go live immediately. Remember your mic is hot after you press start broadcast." />
                        </ListItem>
                        <ListItem component="li">
                            <ListItemText primary="Due to the nature of streaming, please allow a minimum of 15 seconds for people to trickle in. There will be about a 5-10 second delay before users can join. This still beats email." />
                        </ListItem>

                        <ListItem component="li">
                            <ListItemText primary="Only you can see the messages, what you choose to do with these is up to you." />
                        </ListItem>
                        <ListItem component="li">
                            <ListItemText primary="The stream is low latency, but there will still be a 2-7 second delay" />
                        </ListItem>
                        <ListItem component="li">
                            <ListItemText primary="There is about 5-10 seconds of delay with viewer count" />
                        </ListItem>
                        <ListItem component="li">
                            <ListItemText primary="Every 1 hour with 100 viewers cost your AWS bill roughly $7.70" />
                        </ListItem>
                        <ListItem component="li">
                            <ListItemText primary="Emails are shown to help identify the user, but probably best not to share them out loud for privacy reasons." />
                        </ListItem>
                        <ListItem component="li">
                            <ListItemText primary="For the most secure way to end the stream, please click stop instead of just closing the browser tab" />
                        </ListItem>
                    </List>
                </Grid>
                <Grid item xs={6}>
                    {!isStreamLive && <Paper elevation={3}>
                        <Box p={3}>
                            <Typography>The stream chat will appear here. Once started</Typography>

                        </Box>
                    </Paper>}

                    {isStreamLive && !currentStream && <Paper elevation={3}>
                        <Box p={3}>
                            <Typography >Setting up the stream, you are live but this will take a few seconds</Typography>
                            <CircularProgress />
                        </Box>
                    </Paper>}

                    {isStreamLive && currentStream && <CarnivoreStreamComponent stream={currentStream} />}
                </Grid>
            </Grid>



        </>}

    </Container>
}
